import { NgClass, NgTemplateOutlet, TitleCasePipe } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SfoUiJSONSchema7 } from '../../metadata.model';
import { DateVariableInputComponent } from '../csl/date-variable-input/date-variable-input.component';
import { NameInputComponent } from '../csl/name-input/name-input.component';
import { FormService } from '../service/form.service';
import { SchemaKeywords, SfoUiWidgetType } from '../types';
import { RenderMode } from '../types/view.types';
import {
  AddObjectKeyDialogComponent,
  AddObjectKeyDialogResult,
} from './add-object-key-dialog/add-object-key-dialog.component';
import { DynamicInputComponent } from './dynamic-input/dynamic-input.component';
import { EnumInputComponent } from './dynamic-input/enum-input/enum-input.component';
import { HandleSchemaTypePipe } from './handle-schema-type.pipe';
import { HasMultipleKeysPipe } from './has-multiple-keys.pipe';
import { ObjectNotEmptyPipe } from './object-not-empty.pipe';
import { ResolveObjectSchemaPipe } from './resolve-object-schema.pipe';
import { UnsortedKeyvaluePipe } from './unsorted-key-value.pipe';

@Component({
  selector: 'sfo-dynamic-field',
  templateUrl: './dynamic-field.component.html',
  styleUrls: ['./dynamic-field.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgTemplateOutlet,
    DragDropModule,
    MatButtonModule,
    MatExpansionModule,
    MatIconModule,
    MatTooltipModule,
    EnumInputComponent,
    DynamicInputComponent,
    TitleCasePipe,
    ObjectNotEmptyPipe,
    UnsortedKeyvaluePipe,
    HandleSchemaTypePipe,
    ResolveObjectSchemaPipe,
    HasMultipleKeysPipe,
    DateVariableInputComponent,
    NameInputComponent,
  ],
})
export class DynamicFieldComponent {
  private formService: FormService = inject(FormService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private dialog: MatDialog = inject(MatDialog);

  private tableSortState = new WeakMap<FormArray, { key: string; direction: 'asc' | 'desc' }>();

  @Input() schema: SfoUiJSONSchema7 | null | undefined;
  @Input() aFormControl: FormGroup | null;
  @Input() renderMode: RenderMode | undefined;
  @Input() control: string;

  SchemaKeywords = SchemaKeywords;
  SfoUiWidgetType = SfoUiWidgetType;

  shouldShowArrayResetButton(aFormArray: FormArray | null | undefined, schema: SfoUiJSONSchema7) {
    if (!aFormArray || !schema || schema.type !== 'array') {
      return false;
    }

    const defaults = schema.default;
    if (!Array.isArray(defaults)) {
      return false;
    }

    return !this.deepEqualJson(aFormArray.value, defaults);
  }

  resetArrayToDefault(aFormArray: FormArray | null | undefined, schema: SfoUiJSONSchema7) {
    if (!aFormArray || !schema || schema.type !== 'array') {
      return;
    }

    const defaults = schema.default;
    if (!Array.isArray(defaults)) {
      return;
    }

    // Build array structure from defaults to preserve structure/length
    const rebuilt = this.formService.buildFormArray(schema, this.control || 'array', defaults);

    // Set all values to null (reset, not restore defaults)
    const nullValues = defaults.map(() => {
      if (schema.items?.type === 'object' && schema.items.properties) {
        // For object items, create an object with all properties set to null
        const nullObject: Record<string, null> = {};
        Object.keys(schema.items.properties).forEach((key) => {
          nullObject[key] = null;
        });
        return nullObject;
      }
      // For primitive items, just use null
      return null;
    });

    aFormArray.clear();
    for (let i = 0; i < rebuilt.controls.length; i++) {
      const control = rebuilt.controls[i];
      aFormArray.push(control);
      // Set the value to null after adding the control
      if (control instanceof FormGroup) {
        control.patchValue(nullValues[i] as Record<string, null>, { emitEvent: false });
      } else if (control instanceof FormControl) {
        control.setValue(null, { emitEvent: false });
      }
    }

    this.tableSortState.delete(aFormArray);
    aFormArray.markAsDirty();
    aFormArray.updateValueAndValidity({ emitEvent: true });
  }

  dropRow(event: CdkDragDrop<unknown>, aFormArray: FormArray): void {
    if (!aFormArray || event.previousIndex === event.currentIndex) {
      return;
    }

    this.moveRow(event.previousIndex, event.currentIndex, aFormArray);
  }

  moveRow(fromIndex: number, toIndex: number, aFormArray: FormArray): void {
    if (!aFormArray) {
      return;
    }

    const length = aFormArray.length;
    if (fromIndex < 0 || fromIndex >= length || toIndex < 0 || toIndex >= length) {
      return;
    }

    const control = aFormArray.at(fromIndex);
    aFormArray.removeAt(fromIndex);
    aFormArray.insert(toIndex, control);

    aFormArray.markAsDirty();
    aFormArray.updateValueAndValidity({ emitEvent: true });
  }

  moveRowUp(index: number, aFormArray: FormArray): void {
    this.moveRow(index, index - 1, aFormArray);
  }

  moveRowDown(index: number, aFormArray: FormArray): void {
    this.moveRow(index, index + 1, aFormArray);
  }

  isTableColumnSortable(columnSchema: SfoUiJSONSchema7 | undefined | null): boolean {
    const type = columnSchema?.type;
    return type === 'string' || type === 'number' || type === 'integer' || type === 'boolean';
  }

  getSortDirection(aFormArray: FormArray, key: string): 'asc' | 'desc' | null {
    const state = this.tableSortState.get(aFormArray);
    return state?.key === key ? state.direction : null;
  }

  toggleSort(aFormArray: FormArray, key: string, columnSchema: SfoUiJSONSchema7): void {
    if (!this.isTableColumnSortable(columnSchema)) {
      return;
    }

    const previous = this.tableSortState.get(aFormArray);
    const direction: 'asc' | 'desc' =
      previous?.key === key ? (previous.direction === 'asc' ? 'desc' : 'asc') : 'asc';
    this.tableSortState.set(aFormArray, { key, direction });
    this.sortRowsByKey(aFormArray, key, direction, columnSchema);
  }

  private sortRowsByKey(
    aFormArray: FormArray,
    key: string,
    direction: 'asc' | 'desc',
    columnSchema: SfoUiJSONSchema7,
  ): void {
    const controlsWithMeta = aFormArray.controls.map((control, index) => ({
      control,
      index,
      value: (control as FormGroup).get(key)?.value,
    }));

    const compare = (a: any, b: any): number => {
      if (a == null && b == null) return 0;
      if (a == null) return 1;
      if (b == null) return -1;

      if (columnSchema.type === 'boolean') {
        return Number(Boolean(a)) - Number(Boolean(b));
      }

      if (columnSchema.type === 'number' || columnSchema.type === 'integer') {
        const aNum = Number(a);
        const bNum = Number(b);
        if (Number.isNaN(aNum) && Number.isNaN(bNum)) return 0;
        if (Number.isNaN(aNum)) return 1;
        if (Number.isNaN(bNum)) return -1;
        return aNum - bNum;
      }

      const aStr = String(a);
      const bStr = String(b);
      return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
    };

    controlsWithMeta.sort((a, b) => {
      const result = compare(a.value, b.value);
      if (result === 0) {
        return a.index - b.index;
      }
      return direction === 'asc' ? result : -result;
    });

    const sortedControls = controlsWithMeta.map((x) => x.control);
    aFormArray.clear();
    for (const control of sortedControls) {
      aFormArray.push(control);
    }

    aFormArray.markAsDirty();
    aFormArray.updateValueAndValidity({ emitEvent: true });
  }

  private deepEqualJson(a: unknown, b: unknown): boolean {
    if (a === b) return true;

    if (a === null || b === null) return a === b;

    if (Array.isArray(a) || Array.isArray(b)) {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i += 1) {
        if (!this.deepEqualJson(a[i], b[i])) return false;
      }
      return true;
    }

    if (typeof a !== 'object' || typeof b !== 'object') {
      return false;
    }

    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;
    const aKeys = Object.keys(aObj).sort();
    const bKeys = Object.keys(bObj).sort();
    if (aKeys.length !== bKeys.length) return false;
    for (let i = 0; i < aKeys.length; i += 1) {
      const key = aKeys[i];
      if (key !== bKeys[i]) return false;
      if (!this.deepEqualJson(aObj[key], bObj[key])) return false;
    }
    return true;
  }

  addRow(aFormArray: FormArray, schema: SfoUiJSONSchema7) {
    let newControl;

    // Determine the target schema for the new control
    const targetSchema: SfoUiJSONSchema7 | undefined =
      schema?.type === 'array' ? (schema.items as SfoUiJSONSchema7) : schema;

    if (targetSchema?.type === 'object' || targetSchema?.properties) {
      newControl = this.formService.buildFormGroup(targetSchema);
    } else if (targetSchema?.type === 'array' && targetSchema.items) {
      // Nested array
      newControl = this.formService.buildFormArray(targetSchema, 'items');
    } else {
      // Primitive items
      newControl = this.formBuilder.control(undefined);
    }

    try {
      aFormArray.push(newControl);
    } catch (e) {
      console.error('The form array is not an instance of FormArray');
    }
  }

  removeRow(index: number, aFormArray: FormArray) {
    aFormArray.removeAt(index);
    aFormArray.markAsDirty();
    aFormArray.updateValueAndValidity({ emitEvent: true });
  }

  /**
   * Get additional property keys from the form group that are not defined in the schema.
   * @param formGroup The form group to inspect that may contain new data.
   * @param schema The JSON schema to compare against.
   * @returns An array of additional property keys.
   */
  getAdditionalPropertyKeys(
    formGroup: FormGroup | null,
    schema: SfoUiJSONSchema7 | undefined | null,
  ): string[] {
    if (!formGroup || !schema) {
      return [];
    }

    const definedKeys = Object.keys(schema);
    const allKeys = Object.keys(formGroup.controls);
    return allKeys.filter((key) => !definedKeys.includes(key));
  }

  /**
   * Create a new custom property in the form group based on the additionalProperties schema.
   * @param parentGroup
   * @param additionalPropertiesSchema
   * @param parentControlName
   * @returns
   */
  promptAndAddCustomProperty(
    parentGroup: FormGroup | null,
    additionalPropertiesSchema: SfoUiJSONSchema7 | boolean | undefined,
    parentControlName: string | null,
  ): void {
    if (
      !parentGroup ||
      !additionalPropertiesSchema ||
      typeof additionalPropertiesSchema === 'boolean'
    ) {
      console.warn(
        'Cannot add custom property: Invalid parent group or additionalProperties schema.',
        parentGroup,
        additionalPropertiesSchema,
      );
      return;
    }

    const dialogRef = this.dialog.open<AddObjectKeyDialogComponent, any, AddObjectKeyDialogResult>(
      AddObjectKeyDialogComponent,
      {
        height: '20rem',
        width: '40rem',
        data: {
          existingKeys: Object.keys(parentGroup.controls),
          parentName: parentControlName,
        },
      },
    );

    dialogRef.afterClosed().subscribe((dialogFormValues) => {
      if (!dialogFormValues || !Object.keys(dialogFormValues).length) {
        console.warn('No form values provided.');
        return;
      }

      if (parentGroup.get(dialogFormValues.propertyName)) {
        console.error(`This environment name already exists: '${dialogFormValues.propertyName}'`);
        return;
      }

      const propSchema = additionalPropertiesSchema as SfoUiJSONSchema7;
      // #1267 - Allow anything for the custom property name (case-insensitive, including whitespace, etc.)
      // Potentially can break export rendering.
      const newControl = this.formService.buildFormGroup(propSchema, dialogFormValues.propertyName);

      newControl.reset();

      parentGroup.addControl(dialogFormValues.propertyName, newControl);
      parentGroup.updateValueAndValidity({ emitEvent: true });
      parentGroup.markAsDirty();
    });
  }

  removeCustomProperty(
    parentGroup: FormGroup | null,
    propertyName: string | null | undefined,
    controlName: string | null | undefined,
  ): void {
    if (!parentGroup || !propertyName) {
      console.warn('Cannot remove custom property: Invalid parent group or property name.');
      return;
    }

    if (!parentGroup.get(propertyName)) {
      console.warn(`Property '${propertyName}' does not exist in the form group.`);
      return;
    }

    parentGroup.markAsDirty();
    parentGroup.removeControl(propertyName);
    parentGroup.updateValueAndValidity({ emitEvent: true });
  }
}
